import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Box, Typography, Button, Switch, FormControlLabel } from '@mui/material';
import MilestoneTreeView from './MilestoneTreeView'; // Import MilestoneTreeView component
import MilestoneGridView from './MilestoneGridView'; // Import MilestoneGridView component
import MilestoneEditPopup from './MilestoneEditPopup'; // Import MilestoneEditPopup component
import config from '../config/config'; // Import configuration

const KanbanBoard = ({ projectId }) => {
  const [milestones, setMilestones] = useState([]);
  const [tasks, setTasks] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [viewAsTree, setViewAsTree] = useState(true);

  const fetchMilestones = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${config.apiBaseUrl}/api/milestones/project/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
      });
      setMilestones(response.data);
      // Fetch tasks for each milestone
      response.data.forEach(milestone => fetchTasks(milestone.id));
    } catch (error) {
      console.error('Error fetching milestones:', error);
    }
  }, [projectId]);

  useEffect(() => {
    fetchMilestones();
  }, [fetchMilestones]);

  const fetchTasks = async (milestoneId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${config.apiBaseUrl}/api/tasks/milestone/${milestoneId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
      });
      setTasks((prevTasks) => ({ ...prevTasks, [milestoneId]: response.data }));
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleDialogOpen = (milestone = null) => {
    setCurrentMilestone(milestone);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentMilestone(null);
  };

  const handleMilestoneChange = (e, milestoneId) => {
    const { name, value } = e.target;
    setMilestones((prevMilestones) =>
      prevMilestones.map((milestone) =>
        milestone.id === milestoneId ? { ...milestone, [name]: value } : milestone
      )
    );
  };

  const handleTaskChange = (e, taskId, milestoneId) => {
    const { name, value } = e.target;
    setTasks((prevTasks) => {
      const updatedTasks = { ...prevTasks };
      updatedTasks[milestoneId] = updatedTasks[milestoneId].map((task) =>
        task.id === taskId ? { ...task, [name]: value } : task
      );
      return updatedTasks;
    });
  };

  const handleSaveMilestone = async (milestone) => {
    try {
      const token = localStorage.getItem('authToken');
      if (milestone.id) {
        // Update existing milestone
        const response = await axios.put(`${config.apiBaseUrl}/api/milestones/${milestone.id}`, milestone, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
        if (response.status === 200) {
          console.log('Milestone updated successfully');
        } else {
          console.error('Failed to update milestone');
        }
      } else {
        // Create new milestone
        const response = await axios.post(`${config.apiBaseUrl}/api/milestones/create/${projectId}`, milestone, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
        if (response.status === 200) {
          console.log('Milestone created successfully');
        } else {
          console.error('Failed to create milestone');
        }
      }
    } catch (error) {
      console.error('Error saving milestone:', error);
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${config.apiBaseUrl}/api/milestones/${milestoneId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
      });
      fetchMilestones();
    } catch (error) {
      console.error('Error deleting milestone:', error);
    }
  };

  const handleDeleteTask = async (taskId, milestoneId) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${config.apiBaseUrl}/api/tasks/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
      });
      fetchTasks(milestoneId);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }

    const { source, destination } = result;

    console.log('Source:', source);
    console.log('Destination:', destination);

    const sourceMilestoneId = source.droppableId.split('-')[1];
    const destinationMilestoneId = destination.droppableId.split('-')[1];

    console.log('Source Milestone ID:', sourceMilestoneId);
    console.log('Destination Milestone ID:', destinationMilestoneId);

    const sourceTasks = Array.from(tasks[sourceMilestoneId]);
    const movedTaskId = result.draggableId;
    const movedTaskIndex = sourceTasks.findIndex(task => task.id.toString() === movedTaskId);
    const [movedTask] = sourceTasks.splice(movedTaskIndex, 1);

    console.log('Moved Task:', movedTask);

    if (source.droppableId !== destination.droppableId) {
      movedTask.status = destination.droppableId.split('-')[0];
    }

    const destinationTasks = Array.from(tasks[destinationMilestoneId]);

    // Check for duplicates before adding the task to the destination
    const isDuplicate = destinationTasks.some(task => task.id === movedTask.id);
    if (!isDuplicate) {
      destinationTasks.splice(destination.index, 0, movedTask);
    }

    console.log('Updated Source Tasks:', sourceTasks);
    console.log('Updated Destination Tasks:', destinationTasks);

    setTasks((prevTasks) => ({
      ...prevTasks,
      [sourceMilestoneId]: sourceTasks,
      [destinationMilestoneId]: destinationTasks,
    }));

    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${config.apiBaseUrl}/api/tasks/${movedTask.id}`, movedTask, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
      });
      // Refresh tasks after updating
      fetchTasks(sourceMilestoneId);
      fetchTasks(destinationMilestoneId);
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const handleToggleView = () => {
    setViewAsTree(!viewAsTree);
  };

  const handleAddTask = (milestoneId) => {
    const newTask = {
      id: `new-${Date.now()}`, // Temporary ID for new task
      name: '',
      status: 'In Progress', // Default status
      assignedTo: '',
      startDate: '',
      endDate: '',
    };
    setTasks((prevTasks) => ({
      ...prevTasks,
      [milestoneId]: [...(prevTasks[milestoneId] || []), newTask],
    }));
  };

  return (
    <Box sx={{ minHeight: '400px', minWidth: '600px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Milestones</Typography>
        <FormControlLabel
          control={<Switch checked={viewAsTree} onChange={handleToggleView} />}
          label="Edit Milestones"
        />
        <Button variant="contained" color="primary" onClick={() => handleDialogOpen()}>
          Add Milestone
        </Button>
      </Box>
      {viewAsTree ? (
        <MilestoneTreeView
          milestones={milestones}
          tasks={tasks}
          projectId={projectId} 
          handleDialogOpen={handleDialogOpen}
          handleDeleteMilestone={handleDeleteMilestone}
          handleTaskChange={handleTaskChange}
          handleAddTask={handleAddTask}
          handleSaveMilestone={handleSaveMilestone}
          handleDeleteTask={handleDeleteTask}
          handleMilestoneChange={handleMilestoneChange} // Ensure handleMilestoneChange is passed as a prop
          fetchTasks={fetchTasks} // Ensure fetchTasks is passed as a prop
        />
      ) : (
        <MilestoneGridView
          milestones={milestones}
          tasks={tasks}
          projectId={projectId} 
          handleDialogOpen={handleDialogOpen}
          onDragEnd={onDragEnd}
        />
      )}
      <MilestoneEditPopup
        dialogOpen={dialogOpen}
        handleDialogClose={handleDialogClose}
        currentMilestone={currentMilestone}
        handleMilestoneChange={handleMilestoneChange}
        tasks={tasks}
        handleTaskChange={handleTaskChange}
        handleAddTask={handleAddTask}
        handleSaveMilestone={handleSaveMilestone}
      />
    </Box>
  );
};

export default KanbanBoard;